import React, { useState, useEffect, useRef } from 'react';
import { X, Package, ChevronLeft, ChevronRight, Upload, Trash2, Building2, Loader2, ZoomIn, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { addProductImage, removeProductImage } from '../../api/wholesale/companyProducts';
import { useAuth } from '../../contexts/AuthContext';

/**
 * WholesaleProductModal - Modal showing wholesale product details with image gallery
 * Allows admins to upload and delete images
 */
const WholesaleProductModal = ({ isOpen, onClose, product, onProductUpdated }) => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [localImages, setLocalImages] = useState([]);

  const isAdmin = ['super_admin', 'system_admin', 'wholesale_admin'].includes(user?.role);
  const hasImages = localImages.length > 0;

  // Initialize local images from product
  useEffect(() => {
    if (product?.images) {
      setLocalImages(product.images || []);
    }
    setSelectedImageIndex(0);
    setShowLightbox(false);
  }, [product?.id, product?.images]);

  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !showLightbox) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, showLightbox, onClose]);

  if (!isOpen || !product) {
    return null;
  }

  const handlePrevImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const handleNextImage = () => {
    if (selectedImageIndex < localImages.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const result = await addProductImage(product.id, file);
      if (result?.images) {
        setLocalImages(result.images);
        toast.success('Image uploaded successfully');
        onProductUpdated?.(result);
      }
    } catch (err) {
      console.error('Failed to upload image:', err);
      toast.error(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle image delete
  const handleDeleteImage = async (imageIndex) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    setDeleting(true);
    try {
      const result = await removeProductImage(product.id, imageIndex);
      if (result?.images !== undefined) {
        setLocalImages(result.images || []);
        // Adjust selected index if needed
        if (selectedImageIndex >= (result.images?.length || 0)) {
          setSelectedImageIndex(Math.max(0, (result.images?.length || 1) - 1));
        }
        toast.success('Image deleted successfully');
        onProductUpdated?.(result);
      }
    } catch (err) {
      console.error('Failed to delete image:', err);
      toast.error(err.message || 'Failed to delete image');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fadeIn"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900 truncate">
                  {product.tile_code}
                </h2>
                {product.name && (
                  <p className="text-sm text-gray-500 truncate">{product.name}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Image Gallery Section */}
              <div className="space-y-4">
                {/* Main Image */}
                <div className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden group">
                  {hasImages ? (
                    <>
                      <img
                        src={localImages[selectedImageIndex]}
                        alt={`${product.tile_code} - Image ${selectedImageIndex + 1}`}
                        className="w-full h-full object-cover cursor-zoom-in transition-transform duration-300 hover:scale-105"
                        onClick={() => setShowLightbox(true)}
                      />
                      {/* Navigation Arrows */}
                      {localImages.length > 1 && (
                        <>
                          {selectedImageIndex > 0 && (
                            <button
                              onClick={handlePrevImage}
                              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label="Previous image"
                            >
                              <ChevronLeft className="w-5 h-5 text-gray-700" />
                            </button>
                          )}
                          {selectedImageIndex < localImages.length - 1 && (
                            <button
                              onClick={handleNextImage}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                              aria-label="Next image"
                            >
                              <ChevronRight className="w-5 h-5 text-gray-700" />
                            </button>
                          )}
                        </>
                      )}
                      {/* Zoom hint */}
                      <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-1 bg-black/50 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn className="w-3 h-3" />
                        Click to zoom
                      </div>
                      {/* Delete button (admin only) */}
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteImage(selectedImageIndex)}
                          disabled={deleting}
                          className="absolute top-2 right-2 p-2 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                          aria-label="Delete image"
                        >
                          {deleting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                      <Package className="w-24 h-24 mb-4" />
                      <p className="text-sm">No images</p>
                    </div>
                  )}
                </div>

                {/* Thumbnails */}
                {localImages.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {localImages.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`relative flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          idx === selectedImageIndex
                            ? 'border-emerald-500 ring-2 ring-emerald-500/30'
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        <img
                          src={img}
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}

                {/* Upload Button (admin only) */}
                {isAdmin && (
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="product-image-upload"
                    />
                    <label
                      htmlFor="product-image-upload"
                      className={`flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-colors ${
                        uploading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                          <span className="text-sm text-gray-600">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5 text-gray-500" />
                          <span className="text-sm text-gray-600">Upload Image</span>
                        </>
                      )}
                    </label>
                    <p className="mt-1 text-xs text-gray-400 text-center">
                      Max 5MB • JPG, PNG, WebP
                    </p>
                  </div>
                )}
              </div>

              {/* Product Info Section */}
              <div className="space-y-4">
                {/* Tile Code Badge */}
                <div>
                  <span className="text-sm font-mono font-bold px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300">
                    {product.tile_code}
                  </span>
                </div>

                {/* Product Name */}
                {product.name && (
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                    {product.name}
                  </h3>
                )}

                {/* Key Details */}
                <div className="grid grid-cols-1 gap-3">
                  {/* Company */}
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-gray-600">Company</span>
                    </div>
                    <span className="font-semibold text-emerald-700">{product.company_name || 'N/A'}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Size Category</span>
                    <span className="font-semibold text-gray-900">{product.size_category_name || 'N/A'}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Tile Type</span>
                    <span className="font-semibold text-gray-900">{product.tile_type_name || 'N/A'}</span>
                  </div>

                  {product.base_price && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600">Base Price</span>
                      <span className="font-semibold text-gray-900">₦{parseFloat(product.base_price).toLocaleString()}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Unit</span>
                    <span className="font-semibold text-gray-900 capitalize">{product.unit || 'cartons'}</span>
                  </div>
                </div>

                {/* Description */}
                {product.description && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600 block mb-1">Description</span>
                    <p className="text-gray-900">{product.description}</p>
                  </div>
                )}

                {/* Image Count */}
                <p className="text-sm text-gray-500">
                  {localImages.length} image{localImages.length !== 1 ? 's' : ''} 
                  {isAdmin && ' • Click image to zoom, hover to delete'}
                </p>

                {/* Status */}
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    product.is_active !== false
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {product.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox for zoomed image */}
      {showLightbox && hasImages && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowLightbox(false)}
        >
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full text-white"
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Navigation in lightbox */}
          {localImages.length > 1 && selectedImageIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
              className="absolute left-4 p-3 bg-white/20 hover:bg-white/30 rounded-full text-white"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
          {localImages.length > 1 && selectedImageIndex < localImages.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
              className="absolute right-4 p-3 bg-white/20 hover:bg-white/30 rounded-full text-white"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
          
          <img
            src={localImages[selectedImageIndex]}
            alt={`${product.tile_code} - Full size`}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Image counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-white/20 rounded-full text-white text-sm">
            {selectedImageIndex + 1} / {localImages.length}
          </div>
        </div>
      )}
    </>
  );
};

export default WholesaleProductModal;
